<script setup>
import { useNotificationStore } from "@/stores/NotificationStore"
import { showErrorToast } from "@/utils/toast"
import { useI18n } from "vue-i18n"
import { sanitizeHtml } from "@/utils/strings"

const { t } = useI18n()
const notificationStore = useNotificationStore()
//const isSaving = ref(false)

const closedSnackbars = ref(new Set()) // Track closed snackbars by id (snackbars that user manually closed)

// List of visible snackbars (not closed by user)
const visibleSnackbars = computed(() => {
  return notificationStore.snackbarList.filter(
    snackbar => !closedSnackbars.value.has(snackbar.id),
  )
})

const closeSnackbar = async (snackbar) => {
  if (!snackbar || !snackbar.id) {
    showErrorToast(t('notification_invalid'))

    return
  }
  snackbar.isSaving = true

  try {
    const response = await notificationStore.readNotification(snackbar.id)
    if(!response?.success) {
      showErrorToast(response?.message || t('fetch_errors.response_error'))
    } else {
      closedSnackbars.value.add(snackbar.id)
      // Force reactivity update
      closedSnackbars.value = new Set(closedSnackbars.value)
    }
  } catch (e) {
    //
  }

  snackbar.isSaving = false
}

// Get color based on notification type
const getColor = (type) => {
  const colors = {
    success: 'success',
    error: 'error',
    warning: 'warning',
    info: 'info',
  }
  
  return colors[type] || 'primary'
}

// Get icon based on notification type
const getIcon = (type) => {
  const icons = {
    success: 'ri-check-line',
    error: 'ri-error-warning-line',
    warning: 'ri-alert-line',
    info: 'ri-information-line',
  }
  
  return icons[type] || 'ri-notification-line'
}
</script>

<template>
  <div class="snackbar-stack">
    <div
      v-for="(snackbar, index) in visibleSnackbars"
      :key="`snackbar-${snackbar.id}`"
      class="snackbar-item"
      :style="{ '--snackbar-index': index }"
    >
      <VAlert
        :color="getColor(snackbar.type)"
        variant="flat"
        density="comfortable"
        class="snackbar-alert"
      >
        <template #prepend>
          <VIcon :icon="getIcon(snackbar.type)" />
        </template>
        <span v-html="sanitizeHtml(snackbar.message)" />
        <template v-if="snackbar.closable" #append>
          <VBtn
            icon="ri-close-line"
            variant="text"
            size="small"
            density="compact"
            :loading="snackbar.isSaving"
            @click="closeSnackbar(snackbar)"
          />
        </template>
      </VAlert>
    </div>
  </div>
</template>

<style>
.snackbar-stack {
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
  inline-size: calc(100% - 32px);
  inset-block-start: 16px;
  inset-inline-start: 50%;
  max-inline-size: 600px;
  pointer-events: none;
  transform: translateX(-50%);

  .v-progress-circular {
    color: white!important;
  }
}

.snackbar-item {
  animation: slide-in 0.3s ease-out;
  pointer-events: auto;
}

.snackbar-alert {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 15%);
  a {
    color: white !important;
    font-weight: 600;
    text-decoration: underline;
  }
}

@keyframes slide-in {
  from {
    opacity: 0;
    transform: translateY(-100%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
